/**
 * Dynamic Offers Collection Slider Statsig Tester
 */

export {};
declare global {
  interface Window {
    Insider: any
  }
}

import {
  ModelManager,
  getOrCreateStatsigManager,
  buildModelRequestPayload,
  Logger,
  syncDebugFlag,
  getStatsigClient,
  PayloadField,
  getSafeStorage,
  getCartItemsIdsString,
  shouldSendPayload
} from './statsig-manager.js';

interface modelResponse {
  product_handles?: string[];
  useinsider_smart_recommender_id?: string;
}

interface UseInsiderCampaignData {
  campaignId: number
  variationId: number
  products: Array<{
    image_url: string
    name: string
    item_id: string
    sku: string
    size: string
    url: string
    description: string
    in_stock: number
    price: {
      BRL: number
    }
    groupcode: string
    color: string
    locale: string
    product_attributes: {
      activation_date: string
      product_type: string
      quantity: number
      shopify_tag: Array<string>
    }
    category: Array<string>
    discount: {
      BRL: number
    }
    original_price: {
      BRL: number
    }
  }>
}

type ProductsUrlList = Array<{ product_url: string }>
interface SliderElement extends HTMLElement {
  dynamicData: ProductsUrlList;
  status: "notStarted" | "pending" | "pendingForceFallback" | "ready" | "fallbackReady" | "forceNoRender";
  useDynamicRecommender: boolean
}

const MODEL_KEY = "statsig:test:dyo:collection-slider";
const EXPERIMENT = {
  title: "DyoCollection",
  name: "dyo-minicart-recommender",
  gate: "dyo-minicart-recommender",
  layer: "dyo-minicart-recommender",
};

const DEBUG = true; // Altere para false para desativar logs deste teste

if (!customElements.get("dyo-collection-slider"))
  customElements.define(
    "dyo-collection-slider",
    class DynamicOffersCollection extends HTMLElement {
      MODEL_KEY?: string
      customerId?: string;
      statsigGate: string;
      statsigLayer: string;
      modelLink: string | null;
      modelName: string | null;
      useInsiderSmartRecommenderId: string | null;
      statsigStableId: string | null;
      allocatedExperiment: string | null;
      updateTimeout: number;
      isGateEnabled: boolean;
      blockedUTMs: string;
      REQUEST_TIMEOUT_MS: number;
      modelManager: ModelManager;
      statsigManager: Awaited<ReturnType<typeof getOrCreateStatsigManager>>;
      elements: any;
      cart: string | null;
      cartItemsId: string | null;
      sliderId: string | null;
      maxProducts: number;
      objExperimentInfos: {
        stable_id: string;
        allocated_experiment: string;
        gate: string;
        layer: string;
        cart: string;
        customer_id: string;
        category: string;
        component: string;
      };

      constructor() {
        super();
        this.customerId = this.getAttribute("data-customer-id") || null;
        this.sliderId = this.getAttribute("data-slider-id") || null;
        this.statsigGate = this.getAttribute("data-statsig-gate") || EXPERIMENT.gate;
        this.statsigLayer = this.getAttribute("data-statsig-layer") || EXPERIMENT.layer;
        this.MODEL_KEY = `${this.getAttribute("data-model-key")}` || MODEL_KEY;
        this.maxProducts = parseInt(this.getAttribute("data-max-products")) || 0
        this.modelLink = null;
        this.modelName = null;
        this.useInsiderSmartRecommenderId = null
        this.statsigStableId = null;
        this.allocatedExperiment = null;
        this.updateTimeout = 0;
        this.isGateEnabled = this.getAttribute("data-is-gate-enabled") === "true"; //TODO: pegar via parametro
        this.REQUEST_TIMEOUT_MS = 4500;
        this.modelManager = new ModelManager();
        this.objExperimentInfos = {
          stable_id: this.statsigStableId,
          gate: this.statsigGate,
          layer: this.statsigLayer,
          cart: getCartItemsIdsString(),
          customer_id: this.customerId,
          allocated_experiment: this.allocatedExperiment,
          category: "Statsig",
          component: `${EXPERIMENT.name}`,
        }
      }

      async connectedCallback() {
        try {
          syncDebugFlag(EXPERIMENT.name, DEBUG);
          this.statsigManager = await getOrCreateStatsigManager();
          await this.runTest();
        } catch (error) {
          this.logError(`[DEBUG][Statsig][${EXPERIMENT.name}] StatsigManager not ready`, false, error);
        }
      }

      async runTest() {
        try {
          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Iniciando execução do Dynamic Offers Collection`);
          const stableContext = await getStatsigClient()?.getAsyncContext();
          this.objExperimentInfos.stable_id = stableContext?.stableID;
          this.statsigStableId = stableContext?.stableID;

          const gateOpen: boolean = await this.statsigManager.checkGate(this.statsigGate ?? "");
          const shouldPersist = gateOpen;

          if (!shouldPersist) {
            this.forceFallbackInSlider("closedGate");
            Logger.warn(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Gate closed. Exiting without persisting.`, this.objExperimentInfos);
            return;
          }

          // const triggers = ["stable_id"];
          // const shouldSend = shouldSendPayload(triggers, MODEL_KEY, "every");
          // if (!shouldSend) {
          //   Logger.warn(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Not available to send payload. Aborting...`, triggers, this.objExperimentInfos);
          //   return;
          // }
      
          const layer = await this.statsigManager.getLayerParams(this.statsigLayer ?? "");
          if (!layer) {
            this.forceFallbackInSlider("layerNotFound");
            Logger.warn(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Layer not found. Exiting.`, this.objExperimentInfos);
            return;
          }
          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] layer`, layer);

          this.modelLink = layer.get("modelUrl");
          this.modelName = layer.get("modelName");

          let persistedModelData = this.loadModelFromStorage();
          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Dados do modelo persistidos anteriormente:`, persistedModelData);
          if (!persistedModelData || (this.modelName !== "holdout" && this.modelName !== "useinsider_smart_recommender")) {
            /**
             * Quando o modelo for random ou v0 devemos buscar o modelo sempre, pois a resposta pode variar a cada requisição mesmo que já tenhamos algo persistido.
             */
            Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Buscando dados do modelo...`);
            persistedModelData = await this.fetchDOCollectionData();
            Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Dados do modelo recebidos:`, persistedModelData);
          }

          if((this.modelName === "holdout" || this.modelName === "useinsider_smart_recommender")) {
            /**
             * Mesmo que a resposta do modelo sempre seja a mesma, é importante fazer essa requisição para popular informações ao modelo, 
             * aqui não usamos async pois não é necessário aguardar resposta.
             */
            this.fetchDOCollectionData()
          }

          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Dados do modelo atualizado`, persistedModelData);

          if(this.modelName === "useinsider_smart_recommender") this.useInsiderSmartRecommenderId = persistedModelData.useinsider_smart_recommender_id
    
          if((this.modelName !== "holdout" && this.modelName !== "useinsider_smart_recommender")) {
            Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Utilizando lista de produtos do modelo`, persistedModelData);

           const formattedProductUrlsList: ProductsUrlList = persistedModelData.product_handles?.map(product_url => ({ product_url: `https://insiderstore.com.br/products/${product_url}` })) ?? [];
          // const formattedProductUrlsList: ProductsUrlList = persistedModelData.product_handles?.map(product_url => ({ product_url: `http://127.0.0.1:9292/products/${product_url}` })) ?? [];
          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] formattedProductUrlsList:`, formattedProductUrlsList);
            this.persistListInSlider(formattedProductUrlsList, "model");
            return
          }

          if(this.modelName === "holdout") {
            this.hideSmartRecommenderSlider("holdout")
            return;
          };

          if (this.useInsiderSmartRecommenderId) {
           const useInsiderData = await this.fetchUseInsiderCollectionData(this.useInsiderSmartRecommenderId)
           if(!useInsiderData) {
             this.hideSmartRecommenderSlider("useInsiderNotFound")
             Logger.warn(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] useInsiderData not found. Exiting.`, this.objExperimentInfos);
             return;
           }
           const formattedProductUrlsList: ProductsUrlList = useInsiderData?.products?.map(product => ({ product_url: product.url})) ?? [];
           this.persistListInSlider(formattedProductUrlsList, "useInsider");
           return;
          } 

          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Nenhuma useInsiderSmartRecommenderId encontrada`);
          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] force fallback`);
          // this.forceFallbackInSlider("propositalFallback");
          this.hideSmartRecommenderSlider("propositalFallback");

        } catch (error) {
          this.logError(`[DEBUG][Statsig][${EXPERIMENT.name}] Unexpected error in runTest()`, false, error);
        }
      }

      private loadModelFromStorage(): modelResponse | null {
        const storage = getSafeStorage();
        let parsed = null;
        try {
          const raw = storage.getItem(this.MODEL_KEY);
          parsed = raw ? JSON.parse(raw) : null;
        } catch {
          parsed = null;
        }
        if (!parsed || typeof parsed !== "object") return null;
        return parsed;
      }


      async fetchUseInsiderCollectionData(campaignId: string): Promise<UseInsiderCampaignData | null> {
        if (window && window.Insider) {
          async function waitForInsiderLoad() {
            return new Promise((resolve, reject) => {
              window.Insider.eventManager.once(
                "ins-sr:only-api-campaign:load",
                (event, data) => {
                  resolve({ event, data });
                }
              );
            });
          }

         return await waitForInsiderLoad().then(({ _, data }) => {
            if(data && data.campaignId === Number(campaignId)) return data
            return null
          });
        }
      }

      async fetchDOCollectionData() {
        const includeFields: PayloadField[] = [
            "customer_id",
            "stable_id",
            "allocated_experiment",
            "cartItemsIds"
          ];
        const payload = buildModelRequestPayload({
          customer_id: this.objExperimentInfos.customer_id,
          stable_id: this.objExperimentInfos.stable_id,
          allocated_experiment: this.objExperimentInfos.allocated_experiment,
          cartItemsIds: this.objExperimentInfos.cart,
        }, includeFields);

        Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] payload:`, payload);

        try { 
          const data = await this.modelManager.getModel(
            this.modelLink,
            payload,
            { timeout: this.REQUEST_TIMEOUT_MS },
            this.MODEL_KEY,
            includeFields
          );
          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Dados do modelo recebidos:`, data);
          return data;
        } catch (error) {
          this.logError(`[DEBUG][Statsig][${EXPERIMENT.name}] Failed to fetch DOCollectionData via ModelManager`, false, error);
          return null;
        }
      }

      persistListInSlider(productsList: ProductsUrlList, origin: string) {
        const sliderElement = document.querySelector(`#${this.sliderId}`) as SliderElement
        if (sliderElement) {
          const timestamp = `dynamic:${origin}:${this.sliderId}-${new Date().toISOString()}`
          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Persist Product List: ${timestamp}`);
          sliderElement.dynamicData = productsList.slice(0, this.maxProducts || productsList.length)
          sliderElement.dataset.timestamp = timestamp
          return;
        }

         this.logError(`[DEBUG][Statsig][${EXPERIMENT.name}] Slider not founded`, true);
      }

      hideSmartRecommenderSlider(origin: string){
        Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] hideSmartRecommenderSlider`);
        const sliderElementWrapper = document.querySelector(`.products-recommended-dyo`) as HTMLElement
        const sliderElement = document.querySelector(`#${this.sliderId}`) as SliderElement
        if (sliderElementWrapper) {
          sliderElementWrapper.style.display = "none"
          if(sliderElement) {
            const timestamp = `dynamic:${origin}:${this.sliderId}-${new Date().toISOString()}`
            sliderElement.useDynamicRecommender = false
            sliderElement.status = "forceNoRender"
            sliderElement.dataset.timestamp = timestamp
          }
          return;
        }
      }

      forceFallbackInSlider(origin: string){
        const sliderElement = document.querySelector(`#${this.sliderId}`) as SliderElement
        if (sliderElement) {
          const timestamp = `dynamic:${origin}:${this.sliderId}-${new Date().toISOString()}`
          Logger.log(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] Persist Product List: ${timestamp}`);
          sliderElement.useDynamicRecommender = false
          sliderElement.status = "pendingForceFallback"
          sliderElement.dataset.timestamp = timestamp
          return;
        }
        this.logError(`[DEBUG][Statsig][${EXPERIMENT.name}] Slider not founded`, true);
      }

      logError(message: string, showError: boolean = false, error?: Error) {
        Logger.error(EXPERIMENT.name, `[DEBUG][Statsig][${EXPERIMENT.name}] ${message}`, error);
      }
    }
  );